<template>
  <div class="py-4 px-6">
    <div class="d-flex align-center justify-space-between mb-4">
      <div>
        <span
          data-testid="teamActivity-text-weeklyTitle"
          class="text-subtitle-2 font-weight-bold"
        >
          {{ $t('team.activity.weekly_activity_title') }}
        </span>
        &nbsp;
        <span class="text-caption blue-grey--text text--darken-1">
          {{ dateRangeLabel }}
        </span>
      </div>

      <Button
        color="primary"
        plain
        size="small"
        :text="$t('common.button.view_more')"
        @click="navigateToTeamInsights('weekly activity')"
      />
    </div>

    <ActivitySkeleton v-if="isLoading" class="mt-n1 mb-4" />
    <InsightsSummary
      v-else
      :team="team"
      :teamActivity="teamActivity"
      class="mb-4"
    />
    <Alert
      v-if="inactiveUsers > 0"
      :vuetifyProps="{
        class: 'centered-alert-icon mb-0',
        icon: 'mdi-information-outline',
        dismissible: true,
        color: 'blue',
      }"
      :text="
        $t('team.activity.inactivity_alertWithCount', { count: inactiveUsers })
      "
      :buttonText="$t('team.activity.go_to_insights')"
      @input="trackCloseAlert"
      @buttonClick="navigateToTeamInsights('alert')"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import moment from 'moment'

import api from '@/api'

import mixin from '@/mixins/mixin.vue'
import { utils } from 'frontend-js-shared'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import Button from '@/components/UI/Button.vue'
import Alert from '@/components/Alert.vue'
import ActivitySkeleton from '@/components/Activity/ActivitySkeleton.vue'

import InsightsSummary from './InsightsSummary.vue'

export default {
  name: 'WeeklyActivity',

  mixins: [mixin, trackerMixin],

  components: {
    InsightsSummary,
    Button,
    ActivitySkeleton,
    Alert,
  },

  props: {
    team: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      activityBreakdown: null,
      isLoading: true,
      startDate: utils.dates.getMondayOfCurrentWeek(),
      endDate: utils.dates.getSundayOfCurrentWeek(),
    }
  },

  computed: {
    ...mapGetters(['organizationId']),

    inactiveUsers() {
      if (!this.activityBreakdown) return undefined
      return (
        this.activityBreakdown.usersInTeam - this.activityBreakdown.activeUsers
      )
    },

    dateRangeLabel() {
      return `${moment(this.startDate).format('MMM DD')} - ${moment(
        this.endDate
      ).format('MMM DD')}`
    },
  },

  mounted() {
    this.fetchInsights()
  },

  methods: {
    trackCloseAlert(show) {
      if (show) return
      this.trackTeamNavigate({ section: 'alert', element: 'close' })
    },

    navigateToTeamInsights(from) {
      const query = {
        organization: this.organization,
        type: 'teams',
        team: this.team.id,
        startDate: moment(this.startDate).format('YYYY-MM-DD'),
        endDate: moment(this.endDate).format('YYYY-MM-DD'),
        dateOption: 'custom',
      }

      if (this.team.site) {
        query.sites = this.team.site
      }

      this.trackTeamNavigate({
        section: from,
        element: 'insightsButton',
      })

      this.navigateTo({ name: 'reports', query })
    },

    async fetchInsights() {
      try {
        const report = await api.report.getStats(
          this.organizationId,
          'team_activity',
          {
            filters: {
              team: this.$route.params.teamId,
            },
          }
        )
        this.teamActivity = report.data.stats.teamActivity
        this.activityBreakdown = report.data.stats.activityBreakdown
        this.isLoading = false
      } catch (err) {
        this.handleRequestError(err)
      }
    },
  },
}
</script>

<style lang="scss">
.centered-alert-icon {
  & .v-icon {
    align-self: center;
  }
}
</style>
